import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Container } from 'reactstrap';
import { UploadSection } from './UploadSection';
import { StyleGuideChecker } from './StyleGuideChecker';
import PageHeader from '../../common/PageHeader';
import { PageDetails } from '../config';
import { getData, postData } from '../../../DataAccessLayer';
import { pollingStatus } from '../../common/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { esga } from '../../../DataAccessLayer/services';
import {
  unzipDocx,
  readFileAsArrayBuffer,
  getTextFromZip,
  parseDocXmlToParagraphs,
} from './utils/docx';
import { toast } from 'react-toastify';

export const ESGA = () => {
  const id = 'esga';
  const [title, setTitle] = useState('');
  const { sessionId } = useParams();

  const isUploadAllowed = true;
  const checkerRef = useRef(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [docBuffer, setDocBuffer] = useState(null);

  const navigate = useNavigate();

  // ── Page title ──
  useEffect(() => {
    const page = Object.values(PageDetails).find((p) => p.id === id);
    if (page) setTitle(page.title);
  }, []);

  // ── When sessionId is present: fetch doc + poll for Gemini response ──
  useEffect(() => {
    if (!sessionId) return;

    setIsUploaded(true);
    setIsLoadingSession(true);

    // Fetch the docx binary from S3 for preview
    fetchDoc();

    // Poll session until Gemini response is ready
    const stop = pollingStatus({
      fetchFn: fetchSession,
      validate: (data) => data?.STATUS === 'COMPLETED' || data?.STATUS === 'FAILED' || !data,
    });

    return () => stop();
  }, [sessionId]);

  // ── Load doc preview, then feed issues when both are ready ──
  const docLoadedRef = useRef(false);
  const issuesLoadedRef = useRef(false);

  useEffect(() => {
    if (!docBuffer || !checkerRef.current?.loadFile) return;

    docLoadedRef.current = false;
    issuesLoadedRef.current = false;

    checkerRef.current
      .loadFile(docBuffer)
      .then(() => {
        docLoadedRef.current = true;

        // If Gemini response is already available, load issues now
        if (
          !issuesLoadedRef.current &&
          sessionData?.STATUS === 'COMPLETED' &&
          sessionData?.RESPONSE &&
          checkerRef.current?.loadIssues
        ) {
          issuesLoadedRef.current = true;
          setIsLoadingSession(false);
          checkerRef.current.loadIssues(sessionData);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Unable to open the document. Please try again with a valid .docx file.');
        setIsUploaded(false);
      });
  }, [docBuffer]);

  // ── If Gemini response arrives after doc is already loaded ──
  useEffect(() => {
    if (
      docLoadedRef.current &&
      !issuesLoadedRef.current &&
      sessionData?.STATUS === 'COMPLETED' &&
      sessionData?.RESPONSE &&
      checkerRef.current?.loadIssues
    ) {
      issuesLoadedRef.current = true;
      setIsLoadingSession(false);
      checkerRef.current.loadIssues(sessionData);
    }

    if (
      sessionData?.STATUS === 'FAILED' ||
      (sessionData?.STATUS === 'COMPLETED' && !sessionData?.RESPONSE)
    ) {
      toast.error('Error getting response. Please upload document again.');
      setIsLoadingSession(false);
      setIsUploaded(false);
      navigate('/monarch-mind/esga');
    }
  }, [sessionData]);

  // ── Fetch session data (called by polling) ──
  const fetchSession = async () => {
    try {
      const data = await getData(esga.getSession(sessionId), true);

      if (!data) {
        setIsUploaded(false);
        toast.error('No session data found');

        return null;
      }

      setSessionData(data);
      return data;
    } catch (err) {
      toast.error('Error fetching session.');

      return null;
    }
  };

  // ── Fetch doc binary from S3 ──
  const fetchDoc = async () => {
    try {
      const data = await getData(esga.getDoc(sessionId), true, true);

      setDocBuffer(data);
    } catch (err) {
      toast.error('Failed to load document.');
      setIsLoadingSession(false);
      setIsUploaded(false);
      navigate('/monarch-mind/esga');
    }
  };

  // ── Upload handler ──
  const handleUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please upload a .docx file for preview and suggestions.');
      return;
    }

    try {
      setIsLoadingSession(true);

      // Extract paragraphs for Gemini analysis
      const buffer = await readFileAsArrayBuffer(file);
      const zip = await unzipDocx(buffer);
      const xml = await getTextFromZip(zip, 'word/document.xml');
      const { paragraphs: ps } = parseDocXmlToParagraphs(xml);

      const content = {
        paragraphs: ps.map((p) => ({
          paragraphId: p.paragraphId,
          text: p.text,
        })),
      };

      const form = new FormData();
      form.append('file', file);
      form.append('content', JSON.stringify(content));

      const data = await postData(esga.upload, form, true);

      if (data?.SESSION_ID) {
        navigate(`/monarch-mind/esga/${data.SESSION_ID}`);
        return;
      }

      toast.error('Upload succeeded but no session ID returned.');
    } catch (e) {
      console.error(e);
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsLoadingSession(false);
    }
  };

  return (
    <div id={`${id}__div_pageWrapper`} className={`myOdu__${id}`}>
      <Container id={`${id}__container_pageWrapper`} className="myOdu__container secondaryPage">
        <PageHeader id={id} title={title} isSubPage pageConfig={PageDetails} />

        <Box id={`${id}__box_wrapper`} className="wrapper">
          {!isUploaded ? (
            <>
              <UploadSection isUploadAllowed={isUploadAllowed} onUpload={handleUpload} />
            </>
          ) : (
            <StyleGuideChecker
              ref={checkerRef}
              isLoading={isLoadingSession}
              sessionData={sessionData}
            />
          )}
        </Box>
      </Container>
    </div>
  );
};
